/**
 * Mobile navigation drawer.
 *
 * Wires the existing ".menu-toggle" button to a slide-in drawer. All open
 * behaviour is gated behind a (max-width: 1023.98px) media query, so on the
 * protected desktop layout this code does nothing and the hover mega-menu is
 * left entirely alone.
 */
export function initMobileNav() {
	const ready = (fn) => {
		if (document.readyState === 'loading') {
			document.addEventListener('DOMContentLoaded', fn);
		} else {
			fn();
		}
	};

	ready(() => {
		const drawer  = document.getElementById('mobile-drawer');
		const overlay = document.getElementById('mobile-drawer-overlay');
		const toggles = document.querySelectorAll('.menu-toggle');

		if (!drawer || !overlay || !toggles.length) return;

		const mql = window.matchMedia('(max-width: 1023.98px)');
		const closeEls = drawer.querySelectorAll('[data-drawer-close]');
		const overlayClose = overlay.hasAttribute('data-drawer-close') ? overlay : null;
		let lastFocused = null;

		const isMobile = () => mql.matches;

		function openDrawer() {
			if (!isMobile()) return;
			lastFocused = document.activeElement;

			overlay.hidden = false;
			// Force reflow so the transition runs from the hidden state.
			void overlay.offsetWidth;

			drawer.classList.add('is-open');
			overlay.classList.add('is-open');
			drawer.setAttribute('aria-hidden', 'false');
			document.documentElement.classList.add('drawer-open');

			const closeBtn = drawer.querySelector('.mobile-drawer__close');
			if (closeBtn) closeBtn.focus();
		}

		function closeDrawer() {
			drawer.classList.remove('is-open');
			overlay.classList.remove('is-open');
			drawer.setAttribute('aria-hidden', 'true');
			document.documentElement.classList.remove('drawer-open');

			// Hide the overlay from AT once its fade-out finishes.
			window.setTimeout(() => {
				if (!overlay.classList.contains('is-open')) overlay.hidden = true;
			}, 300);

			if (lastFocused && typeof lastFocused.focus === 'function') {
				lastFocused.focus();
			}
		}

		// Open via the existing "Menu" button(s).
		toggles.forEach((btn) => {
			btn.addEventListener('click', (e) => {
				if (!isMobile()) return; // desktop keeps its hover behaviour
				e.preventDefault();
				e.stopPropagation();
				openDrawer();
			});
		});

		// Close controls.
		closeEls.forEach((el) => el.addEventListener('click', closeDrawer));
		if (overlayClose) overlayClose.addEventListener('click', closeDrawer);

		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
				closeDrawer();
			}
		});

		// Tapping any link inside the drawer closes it.
		drawer.querySelectorAll('a').forEach((link) => {
			link.addEventListener('click', () => closeDrawer());
		});

		// Accordion: expand/collapse category panels.
		drawer.querySelectorAll('.mobile-drawer__expander').forEach((btn) => {
			btn.addEventListener('click', () => {
				const panelId = btn.getAttribute('aria-controls');
				const panel = panelId ? document.getElementById(panelId) : null;
				if (!panel) return;

				const willOpen = !panel.classList.contains('is-open');
				panel.classList.toggle('is-open', willOpen);
				btn.setAttribute('aria-expanded', String(willOpen));
			});
		});

		// If the viewport grows back to desktop while the drawer is open,
		// tidy up so nothing lingers behind the protected layout.
		const onChange = () => { if (!isMobile()) closeDrawer(); };
		if (typeof mql.addEventListener === 'function') {
			mql.addEventListener('change', onChange);
		} else if (typeof mql.addListener === 'function') {
			mql.addListener(onChange);
		}
	});
}
